import { useLocation, useNavigate, useParams } from 'react-router';
import { FormProvider, useForm } from 'react-hook-form';

import { PrevHeaderBarWithTitleTemplate } from '#templates';

import ReactQuillEditor from 'src/refactor_shared/ui/react-quill-editor';
import CommunityWriteForms from 'src/refactor_components/community/write/community-write-forms';
import useThreadMutations from '../useThreadMutations';
import { useToast } from '#providers/ToastProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useGetMyAccount } from '#api/accounts';
import { isUser } from 'src/refactor_shared/util/isUser';
import { useSearchParams } from 'react-router-dom';
import sessionStorageManager from '#utils/sessionStorageManager';
import { Modal } from '#molecules';
import { useState } from 'react';
import TrainerCommunityOauthModal from '../Modal/trianer-community-kakao';

type TrainerCommunityWriteProps = {
  category: string;
  title: string;
  content: string;
};

const TrainerCommunityWrite = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const { setBlockedPagePath } = sessionStorageManager();
  const tag = searchParams.get('tag')?.toLocaleUpperCase();

  const location = useLocation();
  const { showToast } = useToast();

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const methods = useForm<TrainerCommunityWriteProps>({
    defaultValues: location.state
      ? location.state
      : { category: tag ? tag : '', title: '', content: '' }
  });

  const { threadMutate } = useThreadMutations({
    id: params.id
  });

  const isValidLoginUser = () => {
    sessionStorage.setItem('prevState', JSON.stringify(methods.getValues()));

    setBlockedPagePath('/trainer-community');
    window.location.href = `${process.env.REACT_APP_API_SERVER_URL}/auth/login/kakao/trainer`;
    return;
  };

  const onSubmit = (data: TrainerCommunityWriteProps) => {
    if (!data.category) {
      showToast({ type: 'fail', message: '카테고리를 선택해주세요' });
      return;
    }
    if (!data.title) {
      showToast({ type: 'fail', message: '제목을 입력해주세요' });
      return;
    }
    if (!data.content || data.content === '<p><br></p>') {
      showToast({ type: 'fail', message: '내용을 입력해주세요' });
      return;
    }

    if (!isUser()) {
      setIsOpen(true);

      return;
    }

    // 모든 유효성 검사를 통과했을 경우 데이터 전송
    threadMutate(data, {
      onSuccess: () => {
        queryClient.invalidateQueries(['get-infinite-trainer-community']);
        navigate('/trainer-community');
      }
    });
  };

  const { data } = useGetMyAccount(true);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(payload => onSubmit(payload))}>
        <PrevHeaderBarWithTitleTemplate
          title="트뮤니티 글 작성"
          defaultBackground
          rightContents={
            <button className="text-[#9170F2] text-[14px]">
              {params.id ? '수정' : '등록'}하기
            </button>
          }
        >
          <CommunityWriteForms>
            <CommunityWriteForms.Categories
              registerName="category"
              options={
                data?.hasWriteColumnPermission
                  ? QUESTION_COLUMN_TYPE_LIST
                  : QUESTION_TYPE_LIST
              }
              tag={tag}
            />
            <CommunityWriteForms.Title
              placeholder="제목을 입력해주세요"
              registerName="title"
            />
            <ReactQuillEditor
              registerName="content"
              placeholder={DEFAULT_PLACEHOLDER[methods.watch('category')]}
            />
          </CommunityWriteForms>
        </PrevHeaderBarWithTitleTemplate>
      </form>
      {isOpen && (
        <Modal onClose={() => setIsOpen(false)}>
          <TrainerCommunityOauthModal
            onClose={() => setIsOpen(false)}
            kakaoLogin={isValidLoginUser}
          />
        </Modal>
      )}
    </FormProvider>
  );
};

export default TrainerCommunityWrite;

const QUESTION_TYPE_LIST = [
  {
    label: '노하우',
    value: 'KNOWHOW'
  },
  {
    label: '일상',
    value: 'DAILY'
  },
  {
    label: '고민',
    value: 'CONCERN'
  }
];

const QUESTION_COLUMN_TYPE_LIST = [
  {
    label: '노하우',
    value: 'KNOWHOW'
  },
  {
    label: '일상',
    value: 'DAILY'
  },
  {
    label: '칼럼',
    value: 'COLUMN'
  },
  {
    label: '고민',
    value: 'CONCERN'
  }
];

const DEFAULT_PLACEHOLDER: Record<string, string> = {
  COLUMN: '트레이너님의 뛰어난 전문성을 공유해 주세요',
  KNOWHOW: '트레이너님의 노하우와 경험을 공유해 주세요',
  CONCERN: '트레이너님의 고민을 공유해 주세요',
  DAILY: '트레이너님의 일상을 공유해 주세요'
};
